import { ReactNode, useContext, useEffect, useState } from 'react';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import * as Sentry from '@sentry/react';

const BASE_URL = import.meta.env.VITE_APP_API_URL;
const TOKEN_KEY = 'ace.token';

interface ILogin {
  children: ReactNode;
}

export function Login({ children }: ILogin) {
  const [loading, setLoading] = useState(true);
  const { state, dispatch } = useContext(DataAccessContext);

  useEffect(() => {
    if (state.user.token.length > 0) {
      Sentry.setUser({
        email: state.user.email,
        id: state.user.clientID,
      });
    }
  }, [state.user]);

  useEffect(() => {
    // If we already have token in store, eit.skip function.
    if (state.user.token.length > 0) {
      setLoading(false);
      return;
    }

    // Attempt to load token from session storage.
    const existing = sessionStorage.getItem(TOKEN_KEY);
    if (existing !== null && existing.length > 0) {
      dispatch({
        type: 'Set auth',
        scope: { parentType: 'user' },
        payload: { JWT: existing },
      });

      setLoading(false);
      return;
    }

    // If we have redirected from Azure Auth, save the token.
    const hash = new URLSearchParams(window.location.hash.substring(1));
    window.location.hash = '';

    const JWT = hash.get('token');
    if (JWT) {
      sessionStorage.setItem(TOKEN_KEY, JWT);

      dispatch({
        type: 'Set auth',
        scope: { parentType: 'user' },
        payload: { JWT },
      });
    }

    const err = hash.get('error');
    if (err) {
      console.error('Login error:', err);

      dispatch({
        type: 'Set error',
        scope: {
          parentType: 'error',
        },
        payload:
          'ACE was not able to sign you in. This either means that something failed, or that your user does not have proper access. Contact your manager if you need access.',
      });
    }

    setLoading(false);
  }, [dispatch, state.user.token]);

  // Effect to handle token expiry.
  useEffect(() => {
    if (
      state.user.token.length < 1 ||
      state.user.tokenExpiration === undefined
    ) {
      return;
    }

    const expiry = state.user.tokenExpiration * 1000 - Date.now();

    const logoutTimer = setTimeout(() => {
      setLoading(true);

      sessionStorage.removeItem(TOKEN_KEY);

      dispatch({
        type: 'Unset auth',
        scope: { parentType: 'user' },
      });
    }, expiry - 10000);

    return () => {
      clearTimeout(logoutTimer);
    };
  }, [dispatch, state.user.token, state.user.tokenExpiration]);

  if (loading) {
    return null;
  }

  if (
    state.user.token.length > 0 &&
    state.user.tokenExpiration &&
    new Date(state.user.tokenExpiration * 1000) > new Date()
  ) {
    return <>{children}</>;
  }

  const url = new URL('/auth/provider/azure/auth', BASE_URL);
  url.searchParams.set('redirect_uri', window.location.href);
  url.searchParams.set('nonce', random());

  window.location.href = url.toString();

  return null;
}

function random() {
  const c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array(50)
    .fill(0)
    .map(() => c.charAt(Math.floor(Math.random() * c.length)))
    .join('');
}
